import React, { useCallback, useContext, useEffect, useState } from "react";
import { getAlbumCover, getArtistPhoto, getArtistPhotos } from "../../components/apis/music-api";
import { FileResponse } from "../../types/FileResponse";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { useMusicStore } from "../../components/store/store";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import NpAi from '../../assets/np-ai.png';
import NpAlbum from '../../assets/np-album.png';
import NpArtist from '../../assets/np-artist.png';
import NpGenre from '../../assets/np-genre.png';
import NpNoRules from '../../assets/np-no-rules.png';
import NpSimilarArtists from '../../assets/np-similar-artists.png';
import NpTrackPl from '../../assets/np-track-pl.png';
import NpTrack from '../../assets/np-track.png';
import NpAlbumPl from '../../assets/np-album-pl.png';
import NpQueue from '../../assets/np-queue.png';
import { PlayModes } from "../../types/PlayModes";
import callApi from "../../components/call-api/call-api";
import CoverDisplay from "../../components/cover-display/CoverDisplay";
import TrackDisplay from "../../components/track-display/TrackDisplay";
import PhotoDisplay from "../../components/photo-display/PhotoDisplay";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { PhotoRating } from "../../types/PhotoRating";

const NowPlaying = () => {
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ cover, setCover ] = useState<FileResponse>();
    const [ logo, setLogo ] = useState<string | undefined>();
    const { currentTrack, playMode, isPlaying } = useMusicStore();
    const [ photoLoading, setPhotoLoading ] = useState(true);
    const [ photo, setPhoto ] = useState<FileResponse>();
    const [ photos, setPhotos ] = useState<string[]>([]);
    const [ artistName, setArtistName ] = useState<string | undefined>();
    const [ albumName, setAlbumName ] = useState<string | undefined>();
    const [rating, setRating] = useLocalStorage('artist-photo-rating', PhotoRating.General);
    const isLessThan1000 = useMediaQuery('(max-width:1000px)');
    const isLessThan500 = useMediaQuery('(max-width:580px)');

    useEffect(() => {
        setPhoto(undefined);
        setPhotos([]);
        if (currentTrack !== undefined) {
            if (currentTrack.album !== albumName) {
                callApi<FileResponse>(getAlbumCover(getAccessToken(), currentTrack.artist, currentTrack.album), setCover);
                setAlbumName(currentTrack?.album);
            }
            
            if (currentTrack.artist !== artistName) {
                callApi<string[]>(getArtistPhotos(getAccessToken(), currentTrack.artist ?? '', rating), setPhotos);
                setArtistName(currentTrack?.artist);
            }
        }
    }, [currentTrack, getAccessToken, artistName, albumName, rating]);

    useEffect(() => {
        const logo = getLogo(playMode);                        
        setLogo(logo);
    }, [playMode]);
    
    useEffect(() => {
        if (photos?.length > 0) {
            const randomIndex = Math.floor(Math.random() * photos.length);
            loadPhoto(photos[randomIndex]);
        }
    }, [photos]);

    const loadPhoto = useCallback((photoName: string) => {
        callApi<FileResponse>(getArtistPhoto(getAccessToken(), artistName ?? '', photoName), setPhoto, setPhotoLoading);
    }, [artistName, getAccessToken]);
    
    const getLogo = (playMode: PlayModes) => {
         switch (playMode) {
            case PlayModes.Genre:
                return NpGenre;
            case PlayModes.Artist:
                return NpArtist;
            case PlayModes.Similar:
                return NpSimilarArtists;
            case PlayModes.NoRules:
                return NpNoRules;
            case PlayModes.AiGenerated:
                return NpAi;
            case PlayModes.Compilation:
            case PlayModes.Album:
                return NpAlbum;
            case PlayModes.CompilationTrack:
            case PlayModes.Track:
                return NpTrack;
            case PlayModes.AlbumPlaylist:
                return NpAlbumPl;
            case PlayModes.TrackPlaylist:
                return NpTrackPl;
            case PlayModes.Queue:
                return NpQueue;
            default:
                return null
        }
    }

    return (
        <Stack>
            <Box>
                <Box
                    component="img"
                    src={logo}
                    sx={{
                        maxHeight: '80px',
                        width: isLessThan500 ? '400px' : '800px'
                    }}
                />
            </Box>
            <Box
                sx={{
                    padding: '24px',
                    border: '6px solid #c7c7c7',
                    borderRadius: '12px',
                    minHeight: '560px',
                }}
            > 
                {isPlaying &&
                    <Stack direction={isLessThan1000 ? 'column' : 'row'}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="column" spacing={1}>
                                <Box>
                                    <Typography variant="h5"><b>{currentTrack?.name}</b></Typography>
                                </Box>
                                <Box>
                                    <TrackDisplay track={currentTrack} />
                                </Box>
                                <Box>                                    
                                    <PhotoDisplay photo={photo} photoLoading={photoLoading} />
                                </Box>
                            </Stack>
                        </Box>
                        <CoverDisplay cover={cover} />
                    </Stack>
                }
            </Box>
        </Stack>
    )
}

export default NowPlaying;